import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, Marker, DirectionsRenderer, InfoWindow } from '@react-google-maps/api';
import { 
  Plus, X, ChevronLeft, ChevronRight, Users, Clock, MapPin,
  AlertTriangle, Car, Calendar, Phone, Navigation, Edit2, Trash2,
  Check, Home, DollarSign, Maximize2, GripVertical, Map, ChevronDown, ChevronUp,
  FileText, Key, MessageSquare, Bell, Camera, RefreshCw, Loader, Mail, UserPlus, Timer, Download,
  Star, History, StickyNote
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { formatJobTime, formatDuration, serviceTypes, OFFICE, jobStatuses } from '../utils/jobData';
import './RouteMapView.css';


// Route colors matching the design spec
const ROUTE_COLORS = {
  office: '#1e3a5f',    // Navy
  routeA: '#b8926a',    // Gold
  routeB: '#87a086',    // Sage
  routeC: '#e07941',    // Orange
  routeD: '#5b8fb9',    // Blue
  routeE: '#9333ea',    // Purple
  routeF: '#dc2626',    // Red
  unassigned: '#9ca3af' // Gray
};

const ROUTE_COLOR_ARRAY = [
  ROUTE_COLORS.routeA,
  ROUTE_COLORS.routeB,
  ROUTE_COLORS.routeC,
  ROUTE_COLORS.routeD,
  ROUTE_COLORS.routeE,
  ROUTE_COLORS.routeF
];

const mapContainerStyle = { width: '100%', height: '350px' };
const defaultCenter = { lat: 38.0293, lng: -78.4767 }; // Charlottesville area
const mapOptions = { 
  disableDefaultUI: false, 
  zoomControl: true, 
  streetViewControl: false, 
  mapTypeControl: false, 
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    }
  ]
};

function RouteMapView({
  jobs,
  employees,
  shifts = [],
  allJobs,
  selectedDate,
  onJobClick, 
  onUpdateJob,
  onOpenReschedule,
  onAddJob,
  onCreateInvoice,
  onMakeRecurring,
  onSendReminder,
  onRunningBehind,
  onAssignTeam,
  onDeleteJob,
  onDeleteSeries,
  getJobInvoice,
  getTeamColor,
  creatingInvoice,
  navigate,
  invoices,
  clients,
  logDispatchActivity,
  isLoaded,
  loadError,
  retry
}) {

  // Map state
  const mapRef = useRef(null);
  const [directions, setDirections] = useState({});
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hoveredJob, setHoveredJob] = useState(null);
  const [showMapMobile, setShowMapMobile] = useState(true);

  // Routes state
  const [routes, setRoutes] = useState([]);
  const [routesLoaded, setRoutesLoaded] = useState(false);
  // Tracks which dateStr the current `routes` state was loaded for.
  // Prevents saves from writing one day's routes into another day's doc
  // when the user navigates between days while a load is in flight.
  const loadedDateRef = useRef(null);
  const [showNewRoute, setShowNewRoute] = useState(false);
  const [newRouteName, setNewRouteName] = useState('');
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(null);
  const [highlightedRoute, setHighlightedRoute] = useState(null);
  const [editingNotesRouteId, setEditingNotesRouteId] = useState(null);
  
  // Mobile state
  const [mobileActiveRoute, setMobileActiveRoute] = useState('unassigned');
  
  // Job detail modal state
  const [selectedJobForModal, setSelectedJobForModal] = useState(null);

  // Job inline editing state
  const [editingField, setEditingField] = useState(null); // 'serviceType' | 'notes' | 'price'
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Series update modal
  const [seriesPrompt, setSeriesPrompt] = useState(null); // { field, value }
  
  // Notify client modal
  const [showNotify, setShowNotify] = useState(false);
  const [notifyType, setNotifyType] = useState('booked');
  const [sendingNotify, setSendingNotify] = useState(false);
  
  // Add Helper state
  const [showHelperPicker, setShowHelperPicker] = useState(false);
  const [showDeleteSeriesConfirm, setShowDeleteSeriesConfirm] = useState(false);

  // Photo viewer state
  const [photoViewer, setPhotoViewer] = useState(null); // { url, type, uploadedAt, employeeId }

  // Sort mode for jobs within each route column
  // 'time' = scheduled time (default), 'status' = active first / completed last, 'revenue' = high $ first
  const [jobSortMode, setJobSortMode] = useState('time');

  // Start editing a field
  const startEditField = (field) => {
    if (!selectedJobForModal) return;
    if (field === 'serviceType') setEditValue(selectedJobForModal.serviceType || 'one_time');
    else if (field === 'notes') setEditValue(selectedJobForModal.notes || '');
    else if (field === 'price') setEditValue(String(selectedJobForModal.price || 0));
    setEditingField(field);
  };

  // Save edited field
  const saveEditField = async (field, value) => {
    if (!selectedJobForModal) return;
    const hasRecurring = selectedJobForModal.recurringScheduleId || selectedJobForModal.isRecurring;

    // For notes: always check if client has future jobs (not just recurring)
    if (field === 'notes') {
      const hasFuture = (allJobs || []).some(j =>
        j.clientId === selectedJobForModal.clientId &&
        j.date > selectedJobForModal.date &&
        j.status !== 'cancelled' &&
        j.id !== selectedJobForModal.id
      );
      if (hasFuture) {
        setSeriesPrompt({ field, value });
        return;
      }
    } else if (hasRecurring && field === 'serviceType') {
      setSeriesPrompt({ field, value });
      return;
    }
    await doSaveField(selectedJobForModal.id, field, value, false);
  };

  const doSaveField = async (jobId, field, value, updateFuture) => {
    setSavingEdit(true);
    try {
      const parsedVal = field === 'price' ? parseFloat(value) || 0 : value;
      if (onUpdateJob) await onUpdateJob(jobId, { [field]: parsedVal });

      if (updateFuture && field === 'notes') {
        // Update all future jobs for this client (regardless of series)
        const futureJobs = (allJobs || []).filter(j =>
          j.clientId === selectedJobForModal.clientId &&
          j.date > selectedJobForModal.date &&
          j.status !== 'cancelled' &&
          j.id !== jobId
        );
        for (const fj of futureJobs) {
          if (onUpdateJob) await onUpdateJob(fj.id, { [field]: parsedVal });
        }
        // Also update recurring schedule template if applicable
        if (selectedJobForModal?.recurringScheduleId) {
          try {
            await setDoc(doc(db, 'recurringSchedules', selectedJobForModal.recurringScheduleId),
              { [field]: parsedVal }, { merge: true });
          } catch (e) { console.error('Failed to update recurring schedule:', e); }
        }
      } else if (updateFuture && selectedJobForModal?.recurringScheduleId) {
        // For non-notes fields (serviceType), use series logic
        const futureJobs = (allJobs || []).filter(j =>
          j.recurringScheduleId === selectedJobForModal.recurringScheduleId &&
          j.date >= selectedJobForModal.date &&
          j.status === 'scheduled' &&
          j.id !== jobId
        );
        for (const fj of futureJobs) {
          if (onUpdateJob) await onUpdateJob(fj.id, { [field]: parsedVal });
        }
        try {
          await setDoc(doc(db, 'recurringSchedules', selectedJobForModal.recurringScheduleId),
            { [field]: parsedVal }, { merge: true });
        } catch (e) { console.error('Failed to update recurring schedule:', e); }
      }

      setSelectedJobForModal(prev => prev ? { ...prev, [field]: parsedVal } : null);
    } catch (e) {
      console.error('Save failed:', e);
      alert('Failed to save. Try again.');
    }
    setSavingEdit(false);
    setEditingField(null);
    setSeriesPrompt(null);
  };

  const handleNotifyClient = async () => {
    if (!selectedJobForModal) return;
    setSendingNotify(true);
    try {
      const job = selectedJobForModal;
      const dateStr2 = format(parseISO(job.date), 'EEEE, MMMM d, yyyy');
      const timeStr = formatJobTime(job.scheduledTime);
      const clientName = job.clientName?.split(' ')[0] || 'there';
      const subject = notifyType === 'booked'
        ? 'Appointment Confirmed - Two Sisters Cleaning'
        : 'Appointment Rescheduled - Two Sisters Cleaning';
      const html = notifyType === 'booked'
        ? `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
            <h2 style="color:#2c5f4e">Your Appointment is Confirmed! ✨</h2>
            <p>Hi ${clientName},</p>
            <p>Your cleaning appointment has been booked:</p>
            <div style="background:#f8f7f5;padding:16px;border-radius:8px;margin:16px 0">
              <p style="margin:4px 0"><strong>Date:</strong> ${dateStr2}</p>
              <p style="margin:4px 0"><strong>Time:</strong> ${timeStr}</p>
              <p style="margin:4px 0"><strong>Address:</strong> ${job.address}</p>
              <p style="margin:4px 0"><strong>Service:</strong> ${serviceTypes[job.serviceType]?.label || job.serviceType}</p>
            </div>
            <p>If you need to make any changes, please contact us.</p>
            <p style="margin-top:24px;color:#6b7280;font-size:14px">— Two Sisters Cleaning & Organizing</p>
          </div>`
        : `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
            <h2 style="color:#2c5f4e">Appointment Update</h2>
            <p>Hi ${clientName},</p>
            <p>Your cleaning appointment has been rescheduled:</p>
            <div style="background:#f8f7f5;padding:16px;border-radius:8px;margin:16px 0">
              <p style="margin:4px 0"><strong>New Date:</strong> ${dateStr2}</p>
              <p style="margin:4px 0"><strong>Time:</strong> ${timeStr}</p>
              <p style="margin:4px 0"><strong>Address:</strong> ${job.address}</p>
            </div>
            <p>If this doesn't work for you, please let us know and we'll find a better time.</p>
            <p style="margin-top:24px;color:#6b7280;font-size:14px">— Two Sisters Cleaning & Organizing</p>
          </div>`;
      
      // Find client email from the client record (job object doesn't always have it)
      const clientRecord = clients?.find(c => c.id === job.clientId);
      const clientEmail = clientRecord?.email || job.clientEmail || '';
      if (!clientEmail) {
        alert('No email found for this client. Add an email to their profile first.');
        setSendingNotify(false);
        return;
      }
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'raw', data: { to: clientEmail, subject, html } })
      });
      alert(`✅ ${notifyType === 'booked' ? 'Booking' : 'Reschedule'} notification sent to ${clientEmail}`);
    } catch (e) {
      console.error('Notify failed:', e);
      alert('Failed to send notification.');
    }
    setSendingNotify(false);
    setShowNotify(false);
  };

  // === ADD / REMOVE HELPER ===
  const addHelper = async (empId) => {
    if (!selectedJobForModal) return;
    const emp = activeEmployees.find(e => e.id === empId);
    if (!emp) return;
    
    const currentHelpers = selectedJobForModal.helpers || [];
    const currentHelperNames = selectedJobForModal.helperNames || [];
    
    if (currentHelpers.includes(empId)) return; // already added
    
    const helperName = `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.`;
    const newHelpers = [...currentHelpers, empId];
    const newHelperNames = [...currentHelperNames, helperName];
    
    await onUpdateJob(selectedJobForModal.id, { helpers: newHelpers, helperNames: newHelperNames });
    setSelectedJobForModal(prev => ({ ...prev, helpers: newHelpers, helperNames: newHelperNames }));
    
    if (logDispatchActivity) {
      logDispatchActivity({
        action: 'helper_added',
        description: `Added ${helperName} as helper to ${selectedJobForModal.clientName}`,
        jobId: selectedJobForModal.id,
        jobDate: selectedJobForModal.date || selectedDate,
        details: { helper: helperName, client: selectedJobForModal.clientName }
      });
    }
    setShowHelperPicker(false);
  };
  
  const removeHelper = async (empId) => {
    if (!selectedJobForModal) return;
    const currentHelpers = selectedJobForModal.helpers || [];
    const currentHelperNames = selectedJobForModal.helperNames || [];
    
    const idx = currentHelpers.indexOf(empId);
    if (idx === -1) return;
    
    const removedName = currentHelperNames[idx] || 'Unknown';
    const newHelpers = currentHelpers.filter(id => id !== empId);
    const newHelperNames = currentHelperNames.filter((_, i) => i !== idx);
    
    await onUpdateJob(selectedJobForModal.id, { helpers: newHelpers, helperNames: newHelperNames });
    setSelectedJobForModal(prev => ({ ...prev, helpers: newHelpers, helperNames: newHelperNames }));
    
    if (logDispatchActivity) {
      logDispatchActivity({
        action: 'helper_removed',
        description: `Removed ${removedName} as helper from ${selectedJobForModal.clientName}`,
        jobId: selectedJobForModal.id,
        jobDate: selectedJobForModal.date || selectedDate,
        details: { helper: removedName, client: selectedJobForModal.clientName }
      });
    }
  };

  // Admin/manager override: set job status manually from dispatch when the
  // cleaner can't or didn't mark it themselves.
  const adminSetJobStatus = async (newStatus) => {
    if (!selectedJobForModal || !onUpdateJob) return;
    const job = selectedJobForModal;
    const prevStatus = job.status;
    if (prevStatus === newStatus) return;

    const nowIso = new Date().toISOString();
    const update = { status: newStatus, statusUpdatedByAdminAt: nowIso };

    if (newStatus === 'en_route') {
      if (!job.enRouteAt) update.enRouteAt = nowIso;
    } else if (newStatus === 'in_progress') {
      if (!job.actual_start) update.actual_start = nowIso;
    } else if (newStatus === 'completed') {
      const endTime = new Date();
      update.actual_end = nowIso;
      update.completedAt = nowIso;
      if (job.actual_start) {
        const startTime = new Date(job.actual_start);
        const durationMin = Math.max(0, Math.round((endTime - startTime) / 60000));
        const durationHours = durationMin / 60;
        update.actual_duration_min = durationMin;
        update.actual_duration_hours = Math.round(durationHours * 100) / 100;

        let laborCost = 0;
        if (job.team && job.team.length > 0) {
          job.team.forEach(empId => {
            const employee = employees.find(e => e.id === empId);
            if (employee?.payRate) {
              laborCost += durationHours * employee.payRate;
            }
          });
        }
        laborCost = Math.round(laborCost * 100) / 100;
        const revenue = job.price || 0;
        update.laborCost = laborCost;
        update.profitMargin = Math.round((revenue - laborCost) * 100) / 100;
        update.laborPercent = revenue > 0 ? Math.round((laborCost / revenue) * 100) : 0;
      }
    } else if (newStatus === 'scheduled') {
      // Revert: clear timestamps so the cleaner workflow can still record
      // the real ones when they happen.
      update.enRouteAt = null;
      update.actual_start = null;
      update.actual_end = null;
      update.completedAt = null;
    }

    await onUpdateJob(job.id, update);
    setSelectedJobForModal(prev => prev ? { ...prev, ...update } : prev);

    if (logDispatchActivity) {
      const prevLabel = jobStatuses[prevStatus]?.label || prevStatus || 'Scheduled';
      const newLabel = jobStatuses[newStatus]?.label || newStatus;
      logDispatchActivity({
        action: 'admin_status_update',
        description: `Admin set ${job.clientName} from ${prevLabel} to ${newLabel}`,
        jobId: job.id,
        jobDate: job.date || selectedDate,
        details: { client: job.clientName, from: prevStatus, to: newStatus }
      });
    }
  };

  // Build a lookup from sorted team-member IDs → route, so enrichment can
  // match jobs to Firebase-persisted routes by team composition rather than
  // relying on the routeId format (route-* vs team_*).
  const routeTeamMap = useMemo(() => {
    const map = {};
    routes.forEach(route => {
      if (route.team && route.team.length > 0) {
        const key = route.team.map(t => t.empId).sort().join('_');
        map[key] = { id: route.id, name: route.name };
      }
    });
    return map;
  }, [routes]);

  // Enrich jobs so every assigned job carries a routeId that matches a
  // visible route column.  Three cases, checked in order:
  //  1. Job already has a routeId that matches an existing route → keep it.
  //  2. Job has a team → find the route with the same team composition.
  //  3. Fallback → generate a deterministic team_* id (creates a new column).
  const enrichedJobs = useMemo(() => {
    return jobs.map(job => {
      // 1. Already points at a known route
      if (job.routeId && routes.some(r => r.id === job.routeId)) {
        return job;
      }

      // 2 & 3. Try to match by team composition, then fall back
      if (job.team && job.team.length > 0) {
        const teamKey = [...job.team].sort().join('_');
        const matched = routeTeamMap[teamKey];
        if (matched) {
          return { ...job, routeId: matched.id, routeName: matched.name };
        }
        // No matching route – use a deterministic id so sync can create one
        return {
          ...job,
          routeId: `team_${teamKey}`,
          routeName: (job.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || null
        };
      }

      return job;
    });
  }, [jobs, routes, routeTeamMap]);

  // Derive route team from job data so route headers always reflect who is working
  const deriveRouteTeam = (routeId, jobList) => {
    const routeJobs = jobList.filter(j => j.routeId === routeId && j.team && j.team.length > 0);
    if (routeJobs.length === 0) return [];
    // Use the first job's team (all jobs in a team-based route share the same team)
    const job = routeJobs[0];
    return (job.team || []).map((empId, i) => ({
      empId,
      isPrimary: i === 0
    }));
  };

  // Get date string for Firebase document ID
  const dateStr = useMemo(() => {
    if (!selectedDate) return format(new Date(), 'yyyy-MM-dd');
    return typeof selectedDate === 'string' ? selectedDate : format(selectedDate, 'yyyy-MM-dd');
  }, [selectedDate]);

  // Load routes from Firebase on date change
  useEffect(() => {
    // Mark routes as not-yet-loaded for this date so the save effect can't
    // flush stale state from the previous date into this date's document.
    setRoutesLoaded(false);
    let cancelled = false;

    const loadRoutes = async () => {
      try {
        const docRef = doc(db, 'dailyRoutes', dateStr);
        const docSnap = await getDoc(docRef);
        if (cancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Backfill team from job data for any route that lost its team info
          const loaded = (data.routes || []).map(r => {
            if (r.team && r.team.length > 0) return r;
            const derived = deriveRouteTeam(r.id, enrichedJobs);
            return derived.length > 0 ? { ...r, team: derived } : r;
          });
          loadedDateRef.current = dateStr;
          setRoutes(loaded);
        } else {
          // No saved routes for this date - start with default routes from jobs
          const existingRouteIds = [...new Set(enrichedJobs.filter(j => j.routeId).map(j => j.routeId))];
          const defaultRoutes = existingRouteIds.map((routeId, idx) => ({
            id: routeId,
            name: `Route ${String.fromCharCode(65 + idx)}`,
            team: deriveRouteTeam(routeId, enrichedJobs),
            color: ROUTE_COLOR_ARRAY[idx % ROUTE_COLOR_ARRAY.length],
            notes: ''
          }));
          loadedDateRef.current = dateStr;
          setRoutes(defaultRoutes);
        }
        setRoutesLoaded(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading routes:', error);
        loadedDateRef.current = dateStr;
        setRoutesLoaded(true);
      }
    };

    loadRoutes();
    return () => { cancelled = true; };
  }, [dateStr]);

  // Save routes to Firebase when they change (including empty array to clean up stale data)
  useEffect(() => {
    if (!routesLoaded) return;
    // Guard against writing the previous day's routes into the new day's doc
    // during the brief window after dateStr changes but before the load for
    // the new date completes.
    if (loadedDateRef.current !== dateStr) return;

    const saveRoutes = async () => {
      try {
        const docRef = doc(db, 'dailyRoutes', dateStr);
        await setDoc(docRef, {
          date: dateStr,
          routes: routes,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      } catch (error) {
        console.error('Error saving routes:', error);
      }
    };

    // Debounce save
    const timeoutId = setTimeout(saveRoutes, 500);
    return () => clearTimeout(timeoutId);
  }, [routes, dateStr, routesLoaded]);

  // Active employees (includes onboarding new hires so they appear on dispatch)
  const activeEmployees = useMemo(() =>
    employees.filter(emp => emp.status === 'active' || emp.status === 'onboarding').map((emp, idx) => ({
      ...emp,
      displayName: `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.`,
    })),
    [employees]
  );

  // Employees with a shift on the selected date. Falls back to all active
  // employees on days where no shifts have been entered, so dispatch keeps
  // working before the shift schedule is built out.
  const scheduledEmployees = useMemo(() => {
    const dayShifts = (shifts || []).filter(
      s => s.date === dateStr && s.status !== 'cancelled'
    );
    if (dayShifts.length === 0) return activeEmployees;
    const scheduledIds = new Set(dayShifts.map(s => s.employeeId));
    return activeEmployees.filter(emp => scheduledIds.has(emp.id));
  }, [activeEmployees, shifts, dateStr]);

  // Persist corrected routeIds back to job documents so the match is durable.
  // Only fires when enrichment re-mapped a job to a different route.
  useEffect(() => {
    if (!routesLoaded) return;
    enrichedJobs.forEach(ej => {
      const original = jobs.find(j => j.id === ej.id);
      if (!original) return;
      // Only persist when the enrichment matched the job to a real route
      // (not a generated team_* fallback) and the stored routeId differs.
      if (ej.routeId && ej.routeId !== original.routeId && !ej.routeId.startsWith('team_')) {
        onUpdateJob(ej.id, { routeId: ej.routeId, routeName: ej.routeName });
      }
    });
  }, [enrichedJobs, routesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync routes with jobs: add routes for new routeIds, remove auto-generated routes that have no jobs
  useEffect(() => {
    if (!routesLoaded) return;

    const jobRouteIds = new Set(enrichedJobs.filter(j => j.routeId).map(j => j.routeId));

    setRoutes(prevRoutes => {
      // Keep routes that have jobs OR were manually created (id starts with "route-")
      const keptRoutes = prevRoutes.filter(r => jobRouteIds.has(r.id) || r.id.startsWith('route-'));

      // Backfill team from job data for any kept route that has no team
      const backfilledRoutes = keptRoutes.map(r => {
        if (r.team && r.team.length > 0) return r;
        const derived = deriveRouteTeam(r.id, enrichedJobs);
        return derived.length > 0 ? { ...r, team: derived } : r;
      });

      // Add routes for routeIds we don't have yet
      const currentRouteIds = new Set(backfilledRoutes.map(r => r.id));
      const newRoutes = [...jobRouteIds]
        .filter(routeId => !currentRouteIds.has(routeId))
        .map((routeId, idx) => ({
          id: routeId,
          name: `Route ${String.fromCharCode(65 + backfilledRoutes.length + idx)}`,
          team: deriveRouteTeam(routeId, enrichedJobs),
          color: ROUTE_COLOR_ARRAY[(backfilledRoutes.length + idx) % ROUTE_COLOR_ARRAY.length],
          notes: ''
        }));

      const updatedRoutes = [...backfilledRoutes, ...newRoutes];
      // Only update state if routes actually changed (check id + team length)
      if (updatedRoutes.length !== prevRoutes.length ||
          updatedRoutes.some((r, i) => r.id !== prevRoutes[i]?.id || r.team.length !== (prevRoutes[i]?.team?.length || 0))) {
        return updatedRoutes;
      }
      return prevRoutes;
    });
  }, [enrichedJobs, routesLoaded]);

  // Get jobs for a route (always time-sorted — drives stop numbers and map order)
  const getRouteJobs = useCallback((routeId) => {
    return enrichedJobs
      .filter(job => job.routeId === routeId)
      .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''));
  }, [enrichedJobs]);

  // Unassigned jobs
  const unassignedJobs = useMemo(() =>
    enrichedJobs
      .filter(job => !job.routeId)
      .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || '')),
    [enrichedJobs]
  );

  // Apply display-only sort. Stop numbers stay tied to time order (computed
  // separately at render) since map directions are always time-sequential.
  const applyDisplaySort = useCallback((list) => {
    if (jobSortMode === 'revenue') {
      return [...list].sort((a, b) => (b.price || 0) - (a.price || 0));
    }
    if (jobSortMode === 'status') {
      const rank = (s) => {
        if (s === 'in_progress') return 0;
        if (s === 'scheduled' || !s) return 1;
        if (s === 'completed') return 2;
        return 3; // cancelled / other
      };
      return [...list].sort((a, b) => rank(a.status) - rank(b.status));
    }
    return list; // 'time' — already time-sorted
  }, [jobSortMode]);

  // Get route color by index
  const getRouteColor = useCallback((routeIndex) => {
    return ROUTE_COLOR_ARRAY[routeIndex % ROUTE_COLOR_ARRAY.length];
  }, []);

  // Calculate route totals
  const getRouteTotals = (routeJobs) => ({
    jobs: routeJobs.length,
    hours: routeJobs.reduce((sum, j) => sum + (j.duration || 0), 0),
    revenue: routeJobs.reduce((sum, j) => sum + (j.price || 0), 0),
    sqft: routeJobs.reduce((sum, j) => sum + (j.sqft || 0), 0)
  });

  // Geocode address to lat/lng with caching
  const geocodeCache = useRef({});
  
  const geocodeAddress = useCallback(async (address) => {
    if (!isLoaded || !window.google || !address) {
      console.log('Geocode skipped - not ready or no address');
      return null;
    }
    
    // Check cache first
    if (geocodeCache.current[address]) {
      console.log('Geocode cache hit:', address);
      return geocodeCache.current[address];
    }
    
    console.log('Geocoding address:', address);
    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        console.log('Geocode result for', address, ':', status);
        if (status === 'OK' && results[0]) {
          const position = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };
          console.log('Geocoded position:', position);
          geocodeCache.current[address] = position;
          resolve(position);
        } else {
          console.warn('Geocode failed for:', address, 'Status:', status);
          resolve(null);
        }
      });
    });
  }, [isLoaded]);

  // Get full address for a job
  const getJobAddress = (job) => {
    const parts = [
      job.address,
      job.city || 'Charlottesville',
      job.state || 'VA',
      job.zip || ''
    ].filter(Boolean);
    return parts.join(', ').trim();
  };

  // Build markers for all jobs - simplified and more robust
  const [markers, setMarkers] = useState([]);
  const [markersLoading, setMarkersLoading] = useState(false);
  
  useEffect(() => {
    if (!isLoaded || !window.google) return;
    
    const buildMarkers = async () => {
      setMarkersLoading(true);
      const newMarkers = [];
      
      // Office marker - always add
      newMarkers.push({
        id: 'office',
        position: { lat: OFFICE.lat, lng: OFFICE.lng },
        type: 'office',
        color: ROUTE_COLORS.office,
        title: 'Office - Start Point'
      });

      // Process all jobs
      const allJobsToProcess = [
        ...unassignedJobs.map(job => ({ job, routeId: null, routeIndex: -1 })),
        ...routes.flatMap((route, routeIndex) => 
          getRouteJobs(route.id).map((job, stopIndex) => ({ 
            job, 
            routeId: route.id, 
            routeIndex,
            stopIndex: stopIndex + 1,
            color: route.color || getRouteColor(routeIndex)
          }))
        )
      ];

      // Geocode all jobs
      for (const item of allJobsToProcess) {
        const { job, routeId, routeIndex, stopIndex, color } = item;
        
        // Try to get position from job data first
        let position = null;
        if (job.lat && job.lng) {
          position = { lat: parseFloat(job.lat), lng: parseFloat(job.lng) };
        } else {
          const address = getJobAddress(job);
          if (address && address.length > 5) {
            position = await geocodeAddress(address);
          }
        }
        
        if (position) {
          newMarkers.push({
            id: job.id,
            position,
            type: routeId ? 'route' : 'unassigned',
            routeId,
            routeIndex,
            color: routeId ? color : ROUTE_COLORS.unassigned,
            job,
            stopNumber: stopIndex || null
          });
        }
      }

      setMarkers(newMarkers);
      setMarkersLoading(false);
      console.log('Built markers:', newMarkers.length, 'markers for', allJobsToProcess.length, 'jobs');
    };

    buildMarkers();
  }, [isLoaded, enrichedJobs, routes, unassignedJobs, getRouteJobs, getRouteColor, geocodeAddress]);

  // Calculate directions for routes
  const calculateRouteDirections = useCallback(async (routeId, routeJobs) => {
    if (!isLoaded || !window.google || routeJobs.length === 0) return;

    const directionsService = new window.google.maps.DirectionsService();
    
    // Build waypoints from job addresses
    const waypoints = [];
    for (const job of routeJobs) {
      const address = getJobAddress(job);
      if (address && address.length > 5) {
        waypoints.push({
          location: address,
          stopover: true
        });
      }
    }

    if (waypoints.length === 0) return;

    const origin = OFFICE.fullAddress;
    let destination, intermediateWaypoints;
    
    if (waypoints.length === 1) {
      destination = waypoints[0].location;
      intermediateWaypoints = [];
    } else {
      destination = waypoints[waypoints.length - 1].location;
      intermediateWaypoints = waypoints.slice(0, -1);
    }

    try {
      const result = await directionsService.route({
        origin,
        destination,
        waypoints: intermediateWaypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false
      });

      setDirections(prev => ({
        ...prev,
        [routeId]: result
      }));
    } catch (error) {
      console.error('Error calculating directions for route', routeId, error);
    }
  }, [isLoaded]);

  // Recalculate directions when routes change
  useEffect(() => {
    if (!isLoaded) return;

    routes.forEach((route) => {
      const routeJobs = getRouteJobs(route.id);
      if (routeJobs.length > 0) {
        calculateRouteDirections(route.id, routeJobs);
      }
    });
  }, [isLoaded, routes, enrichedJobs, getRouteJobs, calculateRouteDirections]);

  // Create custom marker icon
  const createMarkerIcon = useCallback((color, label, isHighlighted = false) => {
    if (!window.google) return null;
    
    const scale = isHighlighted ? 1.3 : 1;
    const size = 32 * scale;
    
    return {
      url: `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 1.25}" viewBox="0 0 32 40">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 8.837 16 24 16 24s16-15.163 16-24C32 7.163 24.837 0 16 0z" fill="${color}" stroke="#fff" stroke-width="2"/>
          <text x="16" y="20" text-anchor="middle" fill="#fff" font-size="12" font-weight="bold" font-family="Arial">${label}</text>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(size, size * 1.25),
      anchor: new window.google.maps.Point(size / 2, size * 1.25)
    };
  }, []);

  // Handle marker click
  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
    if (marker.job) {
      // Scroll job card into view
      const cardElement = document.getElementById(`job-card-${marker.job.id}`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        cardElement.classList.add('highlighted');
        setTimeout(() => cardElement.classList.remove('highlighted'), 2000);
      }
    }
  };

  // Handle job card hover
  const handleJobHover = (job) => {
    setHoveredJob(job?.id || null);
  };

  // Handle job card click - center map on job and open modal
  const handleJobCardClick = (job) => {
    const marker = markers.find(m => m.id === job.id);
    if (marker && mapRef.current) {
      mapRef.current.panTo(marker.position);
      mapRef.current.setZoom(15);
      setSelectedMarker(marker);
    }
    setSelectedJobForModal(job);
  };

  // Handle route header hover - highlight all route markers
  const handleRouteHover = (routeId) => {
    setHighlightedRoute(routeId);
  };

  // Move job between routes
  const moveJob = async (jobId, direction) => {
    const job = enrichedJobs.find(j => j.id === jobId);
    if (!job) return;

    const routeIds = [null, ...routes.map(r => r.id)];
    const currentIndex = routeIds.indexOf(job.routeId || null);
    
    let newIndex;
    if (direction === 'left') {
      newIndex = currentIndex - 1;
      if (newIndex < 0) newIndex = routeIds.length - 1;
    } else {
      newIndex = currentIndex + 1;
      if (newIndex >= routeIds.length) newIndex = 0;
    }

    const newRouteId = routeIds[newIndex];
    const newRoute = routes.find(r => r.id === newRouteId);
    const update = {
      routeId: newRouteId,
      routeName: newRoute?.name || null
    };

    // When moving to a route that has a team, assign that team to the job
    if (newRoute?.team?.length > 0) {
      update.team = newRoute.team.map(t => t.empId);
      update.teamNames = newRoute.team.map(t => {
        const emp = activeEmployees.find(e => e.id === t.empId);
        return emp ? `${emp.firstName} ${emp.lastName?.charAt(0)}.` : '';
      }).filter(Boolean);
      update.primaryTeamMember = newRoute.team.find(t => t.isPrimary)?.empId || null;
    } else if (newRoute && job.team && job.team.length > 0) {
      // Target route has no team — carry the job's team to the route header
      const jobTeam = (job.team || []).map((empId, i) => ({ empId, isPrimary: i === 0 }));
      setRoutes(prev => prev.map(r => r.id === newRoute.id ? { ...r, team: jobTeam } : r));
    }
    // When moving to unassigned (null route), preserve existing team on the job

    await onUpdateJob(jobId, update);
  };

  // Add new route
  const addRoute = () => {
    const routeLetter = String.fromCharCode(65 + routes.length);
    const newRoute = {
      id: `route-${Date.now()}`,
      name: newRouteName.trim() || `Route ${routeLetter}`,
      team: [],
      color: ROUTE_COLOR_ARRAY[routes.length % ROUTE_COLOR_ARRAY.length],
      notes: ''
    };
    
    setRoutes([...routes, newRoute]);
    setNewRouteName('');
    setShowNewRoute(false);
  };

  // Update route notes
  const updateRouteNotes = (routeId, notes) => {
    setRoutes(routes.map(r => 
      r.id === routeId ? { ...r, notes } : r
    ));
  };

  // Toggle team member
  const toggleTeamMember = (routeId, empId) => {
    setRoutes(routes.map(r => {
      if (r.id !== routeId) return r;
      
      const existingIndex = r.team.findIndex(t => t.empId === empId);
      if (existingIndex >= 0) {
        return { ...r, team: r.team.filter(t => t.empId !== empId) };
      } else {
        const isPrimary = r.team.length === 0;
        return { ...r, team: [...r.team, { empId, isPrimary }] };
      }
    }));
  };

  // Save team assignments
  const saveTeamAssignments = async (routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (!route) return;

    const routeJobs = getRouteJobs(routeId);
    const teamMemberIds = route.team.map(t => t.empId);
    const teamNames = route.team.map(t => {
      const emp = activeEmployees.find(e => e.id === t.empId);
      return emp ? `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.` : '';
    }).filter(Boolean);
    const primaryMemberId = route.team.find(t => t.isPrimary)?.empId || null;

    for (const job of routeJobs) {
      // Never wipe an existing team — only update if route actually has members selected
      if (teamMemberIds.length === 0 && job.team && job.team.length > 0) continue;
      await onUpdateJob(job.id, {
        team: teamMemberIds,
        teamNames,
        primaryTeamMember: primaryMemberId,
        routeId: routeId,
        routeName: route.name
      });
    }

    setShowTeamModal(null);
  };

  // Delete route (preserves team assignment on jobs)
  const deleteRoute = async (routeId) => {
    const routeJobs = getRouteJobs(routeId);

    for (const job of routeJobs) {
      await onUpdateJob(job.id, {
        routeId: null,
        routeName: null
      });
    }
    
    setRoutes(routes.filter(r => r.id !== routeId));
    setDirections(prev => {
      const newDirs = { ...prev };
      delete newDirs[routeId];
      return newDirs;
    });
  };

  // Get employee display name
  const getEmployeeById = (empId) => activeEmployees.find(e => e.id === empId);

  // Format end time
  const formatEndTime = (startTime, duration) => {
    if (!startTime) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const endMinutes = hours * 60 + minutes + (duration || 0) * 60;
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    return `${endHours % 12 || 12}:${endMins.toString().padStart(2, '0')} ${endHours >= 12 ? 'PM' : 'AM'}`;
  };

  // Check if client has future appointments
  const clientHasFutureJobs = (clientId, currentJobDate) => {
    if (!allJobs || !clientId) return true;
    return allJobs.some(job => 
      job.clientId === clientId && 
      job.date > currentJobDate &&
      job.status !== 'cancelled'
    );
  };

  return (
    <div className="route-map-view">
      {/* Mobile Map Toggle */}
      <div className="mobile-map-toggle">
        <button
          className={`toggle-btn ${showMapMobile ? 'active' : ''}`}
          onClick={() => setShowMapMobile(!showMapMobile)}
        >
          <Map size={18} />
          {showMapMobile ? 'Hide Map' : 'Show Map'}
          {showMapMobile ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Map Section */}
      <div className={`map-section ${!showMapMobile ? 'hidden-mobile' : ''}`}>
        {loadError && <div className="map-error">Error loading maps{loadError.message ? `: ${loadError.message}` : ''}{retry && <button className="retry-map-btn" onClick={retry}>Retry</button>}</div>}
        {!isLoaded && !loadError && <div className="map-loading">Loading map...</div>}
        {isLoaded && !loadError && <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={11}
          options={mapOptions}
          onLoad={(map) => { mapRef.current = map; }}
        >
          {/* Route direction lines */}
          {routes.map((route, index) => (
            directions[route.id] && (
              <DirectionsRenderer
                key={route.id}
                directions={directions[route.id]}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: route.color || getRouteColor(index),
                    strokeWeight: highlightedRoute === route.id ? 6 : 4,
                    strokeOpacity: highlightedRoute === route.id ? 1 : 0.7
                  }
                }}
              />
            )
          ))}

          {/* Markers */}
          {markers.map((marker) => (
            <Marker
              key={marker.id}
              position={marker.position}
              icon={marker.type === 'office' 
                ? createMarkerIcon(marker.color, '★', false)
                : createMarkerIcon(
                    marker.color, 
                    marker.stopNumber || '•',
                    hoveredJob === marker.id || highlightedRoute === marker.routeId
                  )
              }
              onClick={() => handleMarkerClick(marker)}
              animation={hoveredJob === marker.id ? window.google.maps.Animation.BOUNCE : null}
            />
          ))}

          {/* Info Window */}
          {selectedMarker && selectedMarker.job && (
            <InfoWindow
              position={selectedMarker.position}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="map-info-window">
                <h4>{selectedMarker.job.clientName}</h4>
                <p className="info-time">
                  {formatJobTime(selectedMarker.job.scheduledTime)} - {formatEndTime(selectedMarker.job.scheduledTime, selectedMarker.job.duration)}
                </p>
                {selectedMarker.job.notes && (
                  <p className="info-notes">"{selectedMarker.job.notes}"</p>
                )}
              </div>
            </InfoWindow>
          )}

          {/* Office Info Window */}
          {selectedMarker && selectedMarker.type === 'office' && (
            <InfoWindow
              position={selectedMarker.position}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="map-info-window">
                <h4>🏠 Two Sisters Office</h4>
                <p>{OFFICE.fullAddress}</p>
                <p className="info-notes">Starting point for all routes</p>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>}

        {/* Map Legend */}
        {isLoaded && <div className="map-legend">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: ROUTE_COLORS.office }}>★</span>
            <span>Office</span>
          </div>
          {routes.map((route, index) => (
            <div 
              key={route.id} 
              className={`legend-item ${highlightedRoute === route.id ? 'highlighted' : ''}`}
              onMouseEnter={() => handleRouteHover(route.id)}
              onMouseLeave={() => handleRouteHover(null)}
            >
              <span className="legend-dot" style={{ backgroundColor: route.color || getRouteColor(index) }}></span>
              <span>{route.name}</span>
            </div>
          ))}
          {unassignedJobs.length > 0 && (
            <div className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: ROUTE_COLORS.unassigned }}></span>
              <span>Unassigned</span>
            </div>
          )}
        </div>}
      </div>

      {/* Mobile Route Tabs */}
      <div className="mobile-route-tabs">
        <button 
          className={`route-tab ${mobileActiveRoute === 'unassigned' ? 'active' : ''}`}
          onClick={() => setMobileActiveRoute('unassigned')}
          style={{ borderColor: mobileActiveRoute === 'unassigned' ? ROUTE_COLORS.unassigned : 'transparent' }}
        >
          <span className="tab-dot" style={{ backgroundColor: ROUTE_COLORS.unassigned }}></span>
          {unassignedJobs.length}
        </button>
        {routes.map((route, index) => {
          const routeJobs = getRouteJobs(route.id);
          return (
            <button 
              key={route.id}
              className={`route-tab ${mobileActiveRoute === route.id ? 'active' : ''}`}
              onClick={() => setMobileActiveRoute(route.id)}
              style={{ borderColor: mobileActiveRoute === route.id ? route.color : 'transparent' }}
            >
              <span className="tab-dot" style={{ backgroundColor: route.color || getRouteColor(index) }}></span>
              {route.name.replace('Route ', '')}
            </button>
          );
        })}
        <button className="route-tab add-tab" onClick={() => setShowNewRoute(true)}>
          <Plus size={16} />
        </button>
      </div>

      {/* Route Columns */}
      <div className="routes-section">
        <div className="routes-sort-bar">
          <label className="routes-sort-label">Sort jobs:</label>
          <div className="routes-sort-options">
            <button
              className={`routes-sort-btn ${jobSortMode === 'time' ? 'active' : ''}`}
              onClick={() => setJobSortMode('time')}
            >
              Time
            </button>
            <button
              className={`routes-sort-btn ${jobSortMode === 'status' ? 'active' : ''}`}
              onClick={() => setJobSortMode('status')}
            >
              Status (active first)
            </button>
            <button
              className={`routes-sort-btn ${jobSortMode === 'revenue' ? 'active' : ''}`}
              onClick={() => setJobSortMode('revenue')}
            >
              Revenue
            </button>
          </div>
        </div>
        <div className="route-columns">
          {/* Unassigned Column */}
          <div 
            className={`route-column unassigned ${mobileActiveRoute === 'unassigned' ? 'mobile-active' : ''}`}
          >
            <div className="route-header unassigned-header">
              <div className="route-title">
                <span className="route-icon" style={{ backgroundColor: ROUTE_COLORS.unassigned }}>
                  <AlertTriangle size={14} />
                </span>
                <span>Unassigned</span>
                <span className="job-count">{unassignedJobs.length}</span>
              </div>
            </div>
            
            <div className="route-jobs">
              {unassignedJobs.length === 0 ? (
                <div className="empty-route">
                  <Check size={24} />
                  <p>All jobs assigned!</p>
                </div>
              ) : (
                applyDisplaySort(unassignedJobs).map(job => (
                  <JobCard
                    key={job.id}
                    job={job}
                    color={ROUTE_COLORS.unassigned}
                    onHover={handleJobHover}
                    onClick={handleJobCardClick}
                    onMove={moveJob}
                    onReschedule={onOpenReschedule}
                    hasFutureJobs={clientHasFutureJobs(job.clientId, job.date)}
                    allJobs={allJobs}
                    employees={employees}
                  />
                ))
              )}
            </div>
          </div>

          {/* Route Columns */}
          {routes.map((route, index) => {
            const routeJobs = getRouteJobs(route.id);
            const totals = getRouteTotals(routeJobs);
            const routeColor = route.color || getRouteColor(index);

            return (
              <div 
                key={route.id} 
                className={`route-column ${mobileActiveRoute === route.id ? 'mobile-active' : ''}`}
                onMouseEnter={() => handleRouteHover(route.id)}
                onMouseLeave={() => handleRouteHover(null)}
              >
                <div className="route-header" style={{ borderTopColor: routeColor }}>
                  {/* Row 1: Route Name */}
                  <div className="route-row route-name-row">
                    <span className="route-letter" style={{ backgroundColor: routeColor }}>
                      {route.name.replace('Route ', '').charAt(0)}
                    </span>
                    {editingRouteId === route.id ? (
                      <input
                        type="text"
                        value={route.name}
                        onChange={(e) => setRoutes(routes.map(r => 
                          r.id === route.id ? { ...r, name: e.target.value } : r
                        ))}
                        onBlur={() => setEditingRouteId(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setEditingRouteId(null)}
                        autoFocus
                        className="route-name-input"
                      />
                    ) : (
                      <span 
                        className="route-name"
                        onClick={() => setEditingRouteId(route.id)}
                        title="Click to rename"
                      >
                        {route.name}
                      </span>
                    )}
                  </div>

                  {/* Row 2: Team Assignment */}
                  <div className="route-row route-team-row">
                    <button 
                      className="team-assign-btn"
                      onClick={() => setShowTeamModal(route.id)}
                    >
                      <Users size={14} />
                      {route.team.length > 0 ? (
                        <span className="team-names">
                          {route.team.map(t => {
                            const emp = getEmployeeById(t.empId);
                            return emp ? `${emp.firstName} ${emp.lastName?.charAt(0)}.` : null;
                          }).filter(Boolean).join(', ')}
                        </span>
                      ) : (
                        <span className="no-team">Assign Team</span>
                      )}
                    </button>
                  </div>

                  {/* Row 3: Day Summary Stats */}
                  <div className="route-row route-summary-row">
                    <div className="summary-stat">
                      <span className="stat-value">{totals.jobs}</span>
                      <span className="stat-label">jobs</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-value">{totals.hours}h</span>
                      <span className="stat-label">work</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-value">{totals.sqft > 0 ? Math.round(totals.sqft / 1000) + 'k' : '0'}</span>
                      <span className="stat-label">sqft</span>
                    </div>
                    <div className="summary-stat revenue">
                      <span className="stat-value">${totals.revenue}</span>
                      <span className="stat-label">revenue</span>
                    </div>
                  </div>

                  {/* Row 4: Notes (larger textarea) */}
                  <div className="route-row route-notes-row">
                    {editingNotesRouteId === route.id ? (
                      <textarea
                        className="route-notes-input"
                        value={route.notes || ''}
                        onChange={(e) => updateRouteNotes(route.id, e.target.value)}
                        onBlur={() => setEditingNotesRouteId(null)}
                        placeholder="Add manager notes for the day..."
                        autoFocus
                        rows={3}
                      />
                    ) : (
                      <div 
                        className={`route-notes-display ${route.notes ? 'has-notes' : ''}`}
                        onClick={() => setEditingNotesRouteId(route.id)}
                      >
                        <FileText size={12} />
                        {route.notes || 'Add notes...'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="route-jobs">
                  {applyDisplaySort(routeJobs).map((job) => {
                    // Keep stop number / drive time tied to the time-ordered position
                    // even when the visual sort is by status or revenue.
                    const timeIndex = routeJobs.findIndex(j => j.id === job.id);
                    return (
                      <JobCard
                        key={job.id}
                        job={job}
                        color={routeColor}
                        stopNumber={timeIndex + 1}
                        onHover={handleJobHover}
                        onClick={(job) => setSelectedJobForModal(job)}
                        onMove={moveJob}
                        onReschedule={onOpenReschedule}
                        hasFutureJobs={clientHasFutureJobs(job.clientId, job.date)}
                        allJobs={allJobs}
                        employees={employees}
                        drivingTime={directions[route.id]?.routes?.[0]?.legs?.[timeIndex]?.duration?.text}
                      />
                    );
                  })}
                </div>

                {/* Route Actions */}
                <div className="route-actions">
                  <button 
                    className="route-action-btn edit"
                    onClick={() => setEditingRouteId(route.id)}
                  >
                    <Edit2 size={12} />
                    Rename
                  </button>
                  <button 
                    className="route-action-btn delete"
                    onClick={() => {
                      if (window.confirm(`Delete ${route.name}? Jobs will be moved to Unassigned.`)) {
                        deleteRoute(route.id);
                      }
                    }}
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add Route Column */}
          <div className="route-column add-route-column">
            {showNewRoute ? (
              <div className="new-route-form">
                <input
                  type="text"
                  placeholder={`Route ${String.fromCharCode(65 + routes.length)}`}
                  value={newRouteName}
                  onChange={(e) => setNewRouteName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRoute()}
                  autoFocus
                />
                <div className="new-route-actions">
                  <button onClick={() => setShowNewRoute(false)} className="cancel-btn">
                    <X size={16} />
                  </button>
                  <button onClick={addRoute} className="confirm-btn">
                    <Check size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <button className="add-route-btn" onClick={() => setShowNewRoute(true)}>
                <Plus size={20} />
                <span>Add Route</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Team Assignment Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(null)}>
          <div className="modal-content team-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Users size={20} />
                Assign Team to {routes.find(r => r.id === showTeamModal)?.name}
              </h3>
              <button className="close-btn" onClick={() => setShowTeamModal(null)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="team-list">
              {(() => {
                const route = routes.find(r => r.id === showTeamModal);
                const scheduledIds = new Set(scheduledEmployees.map(e => e.id));
                // Surface team members who are already on the route but off today,
                // so dispatch can still remove them from the assignment.
                const offTodayAssigned = (route?.team || [])
                  .map(t => employees.find(e => e.id === t.empId))
                  .filter(emp => emp && !scheduledIds.has(emp.id))
                  .map(emp => ({
                    ...emp,
                    displayName: `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.`,
                    offToday: true,
                  }));
                return [...scheduledEmployees, ...offTodayAssigned];
              })().map(emp => {
                const route = routes.find(r => r.id === showTeamModal);
                const isAssigned = route?.team.some(t => t.empId === emp.id);
                const isPrimary = route?.team.find(t => t.empId === emp.id)?.isPrimary;

                return (
                  <div
                    key={emp.id}
                    className={`team-member-row ${isAssigned ? 'assigned' : ''} ${emp.offToday ? 'off-today' : ''}`}
                  >
                    <label className="team-checkbox">
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={() => toggleTeamMember(showTeamModal, emp.id)}
                      />
                      <span className="member-name">
                        {emp.firstName} {emp.lastName}
                        {emp.offToday && <span className="off-today-badge">Off today</span>}
                      </span>
                    </label>
                    {isAssigned && (
                      <button 
                        className={`primary-btn ${isPrimary ? 'is-primary' : ''}`}
                        onClick={() => {
                          setRoutes(routes.map(r => {
                            if (r.id !== showTeamModal) return r;
                            return {
                              ...r,
                              team: r.team.map(t => ({ ...t, isPrimary: t.empId === emp.id }))
                            };
                          }));
                        }}
                      >
                        {isPrimary ? '★ Lead' : 'Set Lead'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowTeamModal(null)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={() => saveTeamAssignments(showTeamModal)}>
                Save & Apply to Jobs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Detail Modal — Redesigned */}
      {selectedJobForModal && (
        <div className="modal-overlay job-detail-modal-overlay" style={seriesPrompt || showNotify ? { visibility: 'hidden' } : undefined} onClick={() => { setSelectedJobForModal(null); setShowHelperPicker(false); }}>
          <div className="job-detail-modal enhanced" onClick={e => e.stopPropagation()}>
            
            {/* === HEADER === */}
            <div className="jm-header">
              <div className="jm-avatar">
                {selectedJobForModal.clientName.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="jm-header-info">
                <h3>{selectedJobForModal.clientName}</h3>
                {editingField === 'serviceType' ? (
                  <select
                    className="jm-type-select"
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={() => saveEditField('serviceType', editValue)}
                    autoFocus
                  >
                    {Object.entries(serviceTypes).map(([key, st]) => (
                      <option key={key} value={key}>{st.icon || ''} {st.label}</option>
                    ))}
                  </select>
                ) : (
                  <span 
                    className="jm-type-chip"
                    onClick={(e) => { e.stopPropagation(); startEditField('serviceType'); }}
                    title="Click to change job type"
                  >
                    {serviceTypes[selectedJobForModal.serviceType]?.icon} {serviceTypes[selectedJobForModal.serviceType]?.label || 'Clean'}
                    <span className="jm-type-arrow">▾</span>
                  </span>
                )}
              </div>
              <div className="jm-header-right">
                <span 
                  className="jm-status"
                  style={{ 
                    backgroundColor: jobStatuses[selectedJobForModal.status]?.bg || '#dbeafe',
                    color: jobStatuses[selectedJobForModal.status]?.color || '#1e40af'
                  }}
                >
                  {jobStatuses[selectedJobForModal.status]?.label || 'Scheduled'}
                </span>
                <button className="jm-close" onClick={() => { setSelectedJobForModal(null); setShowHelperPicker(false); }}>
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* === SCROLLABLE BODY === */}
            <div className="jm-body">

              {/* Contact Bar */}
              <div className="jm-contact-bar">
                {selectedJobForModal.clientPhone && (
                  <a href={`tel:${selectedJobForModal.clientPhone}`} className="jm-contact-pill">
                    <Phone size={15} /> Call
                  </a>
                )}
                <button className="jm-contact-pill" onClick={() => {
                  if (selectedJobForModal.clientPhone) window.open(`sms:${selectedJobForModal.clientPhone}`, '_self');
                }}>
                  <MessageSquare size={15} /> Text
                </button>
              </div>

              {/* Schedule & Location */}
              <div className="jm-section">
                <div className="jm-section-label">
                  <span>Schedule & Location</span>
                  <button className="jm-section-action" onClick={() => {
                    setSelectedJobForModal(null);
                    if (onOpenReschedule) onOpenReschedule(selectedJobForModal);
                  }}>
                    <Edit2 size={11} /> Reschedule
                  </button>
                </div>
                <div className="jm-info-row">
                  <Calendar size={15} />
                  <span>{format(parseISO(selectedJobForModal.date), 'EEE, MMM d, yyyy')}</span>
                </div>
                <div className="jm-info-row">
                  <Clock size={15} />
                  <span>{formatJobTime(selectedJobForModal.scheduledTime)} – {formatJobTime(selectedJobForModal.scheduledEndTime)}</span>
                </div>
                <div className="jm-info-row">
                  <MapPin size={15} />
                  <div>
                    <span>{selectedJobForModal.address}</span>
                    <span className="jm-muted">{selectedJobForModal.city || 'Charlottesville'}, {selectedJobForModal.state || 'VA'}</span>
                  </div>
                </div>
                {(selectedJobForModal.driveTimeFromPrev != null || selectedJobForModal.driveDistanceFromPrev != null) && (
                  <div className="jm-info-row">
                    <Car size={15} />
                    <span className="jm-muted">{selectedJobForModal.driveTimeFromPrev ?? '--'} min • {selectedJobForModal.driveDistanceFromPrev ?? '--'} mi from prev</span>
                  </div>
                )}
                <a 
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedJobForModal.fullAddress || (selectedJobForModal.address + ', ' + (selectedJobForModal.city || 'Charlottesville') + ', ' + (selectedJobForModal.state || 'VA')))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="jm-directions-btn"
                >
                  <Navigation size={15} /> Get Directions
                </a>
              </div>

              {/* Route & Team — merged */}
              <div className="jm-section">
                <div className="jm-section-label"><span>Route & Team</span></div>
                <div className="jm-route-pills">
                  <button 
                    className={`jm-route-pill ${!selectedJobForModal.routeId ? 'active' : ''}`}
                    onClick={() => {
                      const oldRoute = selectedJobForModal.routeName || 'Unassigned';
                      onUpdateJob(selectedJobForModal.id, { routeId: null, routeName: null });
                      if (logDispatchActivity && oldRoute !== 'Unassigned') {
                        logDispatchActivity({
                          action: 'route_move',
                          description: `Moved ${selectedJobForModal.clientName} from ${oldRoute} to Unassigned`,
                          jobId: selectedJobForModal.id,
                          jobDate: selectedJobForModal.date || selectedDate,
                          details: { from: oldRoute, to: 'Unassigned', client: selectedJobForModal.clientName }
                        });
                      }
                      // Update local state - preserve team assignment
                      setSelectedJobForModal(prev => ({ ...prev, routeId: null, routeName: null }));
                    }}
                  >
                    Unassigned
                  </button>
                  {routes.map((route, idx) => (
                    <button 
                      key={route.id}
                      className={`jm-route-pill ${selectedJobForModal.routeId === route.id ? 'active' : ''}`}
                      style={{ '--rc': route.color || getRouteColor(idx) }}
                      onClick={() => {
                        const oldRoute = selectedJobForModal.routeName || 'Unassigned';
                        const update = { routeId: route.id, routeName: route.name };

                        // Only overwrite job team if route has a team assigned
                        if (route.team.length > 0) {
                          update.team = route.team.map(t => t.empId);
                          update.teamNames = route.team.map(t => {
                            const emp = getEmployeeById(t.empId);
                            return emp ? `${emp.firstName} ${emp.lastName?.charAt(0)}.` : '';
                          }).filter(Boolean);
                        } else if (selectedJobForModal.team && selectedJobForModal.team.length > 0) {
                          // Route has no team — carry the job's team to the route header
                          const jobTeam = (selectedJobForModal.team || []).map((empId, i) => ({ empId, isPrimary: i === 0 }));
                          setRoutes(prev => prev.map(r => r.id === route.id ? { ...r, team: jobTeam } : r));
                        }

                        onUpdateJob(selectedJobForModal.id, update);
                        if (logDispatchActivity) {
                          logDispatchActivity({
                            action: 'route_move',
                            description: `Moved ${selectedJobForModal.clientName} from ${oldRoute} to ${route.name}`,
                            jobId: selectedJobForModal.id,
                            jobDate: selectedJobForModal.date || selectedDate,
                            details: { from: oldRoute, to: route.name, client: selectedJobForModal.clientName }
                          });
                        }
                        // Update local state — preserve team if route has none
                        setSelectedJobForModal(prev => ({
                          ...prev,
                          routeId: route.id,
                          routeName: route.name,
                          ...(route.team.length > 0 ? {
                            team: route.team.map(t => t.empId),
                            teamNames: route.team.map(t => {
                              const emp = getEmployeeById(t.empId);
                              return emp ? `${emp.firstName} ${emp.lastName?.charAt(0)}.` : '';
                            }).filter(Boolean)
                          } : {})
                        }));
                      }}
                    >
                      <span className="jm-route-dot" style={{ backgroundColor: route.color || getRouteColor(idx) }}></span>
                      {route.name}
                    </button>
                  ))}
                </div>
                {/* Show team members for current route */}
                {selectedJobForModal.teamNames && selectedJobForModal.teamNames.length > 0 ? (
                  <div className="jm-team-badges">
                    {selectedJobForModal.teamNames.map((name, i) => (
                      <span key={name} className="jm-team-badge" style={{ 
                        borderLeftColor: getTeamColor ? getTeamColor(name) : '#6b7280' 
                      }}>
                        {i === 0 && '★ '}{name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="jm-no-team">
                    <AlertTriangle size={14} /> No team assigned — pick a route above
                  </div>
                )}

                {/* === Helpers Section === */}
                <div className="jm-helpers-section">
                  <div className="jm-helpers-label">
                    <span>Helpers</span>
                    <button 
                      className="jm-add-helper-btn" 
                      onClick={(e) => { e.stopPropagation(); setShowHelperPicker(!showHelperPicker); }}
                    >
                      <UserPlus size={12} /> Add Helper
                    </button>
                  </div>

                  {/* Current helpers */}
                  {(selectedJobForModal.helpers?.length > 0) ? (
                    <div className="jm-helper-badges">
                      {(selectedJobForModal.helperNames || []).map((name, i) => (
                        <span key={i} className="jm-helper-badge">
                          <span className="jm-helper-tag">Helper</span>
                          {name}
                          <button className="jm-helper-remove" onClick={(e) => { 
                            e.stopPropagation(); 
                            removeHelper(selectedJobForModal.helpers[i]); 
                          }}>
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    !showHelperPicker && <div className="jm-no-helpers">No helpers assigned</div>
                  )}

                  {/* Helper picker dropdown */}
                  {showHelperPicker && (
                    <div className="jm-helper-picker">
                      <div className="jm-helper-picker-title">Select helper from another team</div>
                      <div className="jm-helper-list">
                        {scheduledEmployees
                          .filter(emp => {
                            // Exclude people already on this job's team or already a helper
                            const onTeam = (selectedJobForModal.team || []).includes(emp.id);
                            const isHelper = (selectedJobForModal.helpers || []).includes(emp.id);
                            return !onTeam && !isHelper;
                          })
                          .map(emp => {
                            // Check what route they're on today
                            const empRoute = routes.find(r => r.team?.some(t => t.empId === emp.id));
                            return (
                              <button 
                                key={emp.id} 
                                className="jm-helper-option"
                                onClick={(e) => { e.stopPropagation(); addHelper(emp.id); }}
                              >
                                <div className="jm-helper-option-avatar" style={{ background: emp.color || '#6b7280' }}>
                                  {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                                </div>
                                <div className="jm-helper-option-info">
                                  <span className="jm-helper-option-name">{emp.displayName}</span>
                                  <span className="jm-helper-option-route">
                                    {empRoute ? `On ${empRoute.name}` : 'No route today'}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        {scheduledEmployees.filter(emp =>
                          !(selectedJobForModal.team || []).includes(emp.id) &&
                          !(selectedJobForModal.helpers || []).includes(emp.id)
                        ).length === 0 && (
                          <div className="jm-no-helpers">All scheduled employees are already assigned</div>
                        )}
                      </div>
                      <button className="jm-helper-picker-close" onClick={() => setShowHelperPicker(false)}>
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="jm-section jm-stats-section">
                <div className="jm-stats-row">
                  <div className="jm-stat">
                    <span className="jm-stat-val">{selectedJobForModal.duration || '--'}h</span>
                    <span className="jm-stat-lbl">Duration</span>
                  </div>
                  {selectedJobForModal.sqft > 0 && (
                    <div className="jm-stat">
                      <span className="jm-stat-val">{Number(selectedJobForModal.sqft).toLocaleString()}</span>
                      <span className="jm-stat-lbl">Sq Ft</span>
                    </div>
                  )}
                  {selectedJobForModal.bedrooms > 0 && (
                    <div className="jm-stat">
                      <span className="jm-stat-val">{selectedJobForModal.bedrooms}</span>
                      <span className="jm-stat-lbl">Beds</span>
                    </div>
                  )}
                  {selectedJobForModal.bathrooms > 0 && (
                    <div className="jm-stat">
                      <span className="jm-stat-val">{selectedJobForModal.bathrooms}</span>
                      <span className="jm-stat-lbl">Baths</span>
                    </div>
                  )}
                  <div className="jm-stat" onClick={(e) => { e.stopPropagation(); startEditField('price'); }} style={{ cursor: 'pointer' }} title="Click to edit price">
                    {editingField === 'price' ? (
                      <input
                        className="jm-price-input"
                        type="number"
                        step="5"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onBlur={() => saveEditField('price', editValue)}
                        onKeyDown={e => { if (e.key === 'Enter') saveEditField('price', editValue); if (e.key === 'Escape') setEditingField(null); }}
                        autoFocus
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span className="jm-stat-val jm-price">${selectedJobForModal.price || 0}</span>
                    )}
                    <span className="jm-stat-lbl">Revenue</span>
                  </div>
                </div>
              </div>

              {/* Property details - pets */}
              {selectedJobForModal.pets && (
                <div className="jm-detail-tag">
                  🐾 Pets: {typeof selectedJobForModal.pets === 'string' ? selectedJobForModal.pets : 'Yes'}{selectedJobForModal.petDetails ? ` — ${selectedJobForModal.petDetails}` : ''}
                </div>
              )}

              {/* Notes Section — all note types stacked with color coding */}
              <div className="jm-section">
                <div className="jm-section-label">
                  <span>Notes</span>
                  {editingField !== 'notes' && (
                    <button className="jm-section-action" onClick={(e) => { e.stopPropagation(); startEditField('notes'); }}>
                      <Edit2 size={11} /> Edit Job Notes
                    </button>
                  )}
                </div>

                {/* Access Instructions */}
                {(selectedJobForModal.accessInstructions || selectedJobForModal.accessInfo) && (
                  <div className="jm-note access">
                    <div className="jm-note-label">🔑 Access</div>
                    {selectedJobForModal.accessInstructions || selectedJobForModal.accessInfo}
                  </div>
                )}

                {/* Client Notes */}
                {selectedJobForModal.clientNotes && (
                  <div className="jm-note client">
                    <div className="jm-note-label">📝 Client Notes</div>
                    {selectedJobForModal.clientNotes}
                  </div>
                )}

                {/* Job Notes — editable */}
                {editingField === 'notes' ? (
                  <div className="jm-edit-area" onClick={e => e.stopPropagation()}>
                    <textarea
                      className="jm-edit-textarea"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      rows={3}
                      placeholder="Add notes about this job..."
                      autoFocus
                    />
                    <div className="jm-edit-btns">
                      <button className="jm-save-btn" onClick={() => saveEditField('notes', editValue)} disabled={savingEdit}>
                        {savingEdit ? 'Saving...' : 'Save'}
                      </button>
                      <button className="jm-cancel-btn" onClick={() => setEditingField(null)}>Cancel</button>
                    </div>
                  </div>
                ) : selectedJobForModal.notes ? (
                  <div className="jm-note job" onClick={(e) => { e.stopPropagation(); startEditField('notes'); }} title="Click to edit">
                    <div className="jm-note-label">📋 Job Notes</div>
                    {selectedJobForModal.notes}
                  </div>
                ) : (
                  <div className="jm-note empty" onClick={(e) => { e.stopPropagation(); startEditField('notes'); }}>
                    No job notes — tap to add
                  </div>
                )}

                {/* Special Instructions */}
                {selectedJobForModal.specialInstructions && (
                  <div className="jm-note special">
                    <div className="jm-note-label">⚠️ Special</div>
                    {selectedJobForModal.specialInstructions}
                  </div>
                )}
              </div>

              {/* === PHOTOS SECTION === */}
              {(() => {
                const photos = selectedJobForModal.photos || [];
                const beforePhotos = photos.filter(p => p.type === 'before');
                const afterPhotos = photos.filter(p => p.type === 'after');
                const damagePhotos = photos.filter(p => p.type?.startsWith('damage'));
                const damageReports = selectedJobForModal.damageReports || [];
                if (photos.length === 0 && damageReports.length === 0) return null;

                const getEmployeeName = (empId) => {
                  if (!empId || !employees) return null;
                  const emp = employees.find(e => e.id === empId);
                  return emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : null;
                };

                const handleDownloadPhoto = async (photo) => {
                  try {
                    const response = await fetch(photo.url);
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = photo.filename || `${photo.type || 'photo'}_${Date.now()}.jpg`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Download failed:', err);
                    window.open(photo.url, '_blank');
                  }
                };

                const handleDownloadAll = async () => {
                  for (const photo of photos) {
                    await handleDownloadPhoto(photo);
                    await new Promise(r => setTimeout(r, 300));
                  }
                };

                return (
                  <div className="jm-section">
                    <div className="jm-section-label">
                      <span><Camera size={13} /> Photos ({photos.length})</span>
                      {photos.length > 0 && (
                        <button className="jm-section-action" onClick={(e) => { e.stopPropagation(); handleDownloadAll(); }}>
                          <Download size={11} /> Download All
                        </button>
                      )}
                    </div>

                    {beforePhotos.length > 0 && (
                      <div className="jm-photo-group">
                        <span className="jm-photo-group-label">Before ({beforePhotos.length})</span>
                        <div className="jm-photo-thumbs">
                          {beforePhotos.map((p, i) => (
                            <div key={p.id || i} className="jm-photo-thumb" onClick={(e) => { e.stopPropagation(); setPhotoViewer(p); }}>
                              <img src={p.url} alt={`Before ${i + 1}`} loading="lazy" />
                              {getEmployeeName(p.employeeId) && (
                                <span className="jm-photo-uploader">{getEmployeeName(p.employeeId)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {afterPhotos.length > 0 && (
                      <div className="jm-photo-group">
                        <span className="jm-photo-group-label">After ({afterPhotos.length})</span>
                        <div className="jm-photo-thumbs">
                          {afterPhotos.map((p, i) => (
                            <div key={p.id || i} className="jm-photo-thumb" onClick={(e) => { e.stopPropagation(); setPhotoViewer(p); }}>
                              <img src={p.url} alt={`After ${i + 1}`} loading="lazy" />
                              {getEmployeeName(p.employeeId) && (
                                <span className="jm-photo-uploader">{getEmployeeName(p.employeeId)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {damagePhotos.length > 0 && (
                      <div className="jm-photo-group damage">
                        <span className="jm-photo-group-label">Damage ({damagePhotos.length})</span>
                        <div className="jm-photo-thumbs">
                          {damagePhotos.map((p, i) => (
                            <div key={p.id || i} className="jm-photo-thumb" onClick={(e) => { e.stopPropagation(); setPhotoViewer(p); }}>
                              <img src={p.url} alt={`Damage ${i + 1}`} loading="lazy" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {damageReports.length > 0 && (
                      <div className="jm-damage-reports">
                        {damageReports.map((dr, i) => (
                          <div key={dr.id || i} className="jm-damage-report">
                            <div className="jm-damage-header">
                              <span className={`jm-damage-type ${dr.type}`}>
                                {dr.type === 'existing' ? 'Pre-existing' : 'New Damage'}
                              </span>
                              {dr.reportedAt && (
                                <span className="jm-damage-time">
                                  {(() => { try { return format(parseISO(dr.reportedAt), 'h:mm a'); } catch { return ''; } })()}
                                </span>
                              )}
                              {getEmployeeName(dr.reportedBy) && (
                                <span className="jm-damage-reporter">by {getEmployeeName(dr.reportedBy)}</span>
                              )}
                            </div>
                            <p className="jm-damage-desc">{dr.description}</p>
                            {dr.photos?.length > 0 && (
                              <div className="jm-photo-thumbs">
                                {dr.photos.map((p, pi) => (
                                  <div key={p.id || pi} className="jm-photo-thumb" onClick={(e) => { e.stopPropagation(); setPhotoViewer(p); }}>
                                    <img src={p.url} alt={`Damage report ${i + 1}`} loading="lazy" />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* === ACTIONS FOOTER — sticky at bottom === */}
            <div className="jm-footer">
              {/* Row 1: Primary actions */}
              <div className="jm-primary-actions">
                {getJobInvoice && getJobInvoice(selectedJobForModal.id) ? (
                  <button className="jm-btn invoice" onClick={() => {
                    setSelectedJobForModal(null);
                    if (navigate) navigate(`/admin/invoices/${getJobInvoice(selectedJobForModal.id).id}`);
                  }}>
                    <FileText size={15} /> View Invoice
                  </button>
                ) : (
                  <button className="jm-btn invoice" onClick={() => {
                    setSelectedJobForModal(null);
                    if (onCreateInvoice) onCreateInvoice(selectedJobForModal);
                  }} disabled={creatingInvoice === selectedJobForModal.id}>
                    {creatingInvoice === selectedJobForModal.id ? (
                      <><Loader size={15} className="spin" /> Creating...</>
                    ) : (
                      <><FileText size={15} /> Create Invoice</>
                    )}
                  </button>
                )}
                {!selectedJobForModal.isRecurring && selectedJobForModal.status === 'scheduled' && (
                  <button className="jm-btn recurring" onClick={() => {
                    setSelectedJobForModal(null);
                    if (onMakeRecurring) onMakeRecurring(selectedJobForModal);
                  }}>
                    <RefreshCw size={15} /> Make Recurring
                  </button>
                )}
              </div>

              {/* Row 1b: Admin status override — manually mark statuses
                  when the cleaner can't / didn't update from the field. */}
              {selectedJobForModal.status !== 'cancelled' && (
                <div className="jm-comm-group">
                  <div className="jm-comm-label">Admin Status Override</div>
                  <div className="jm-status-row">
                    {selectedJobForModal.status !== 'en_route' &&
                      selectedJobForModal.status !== 'in_progress' &&
                      selectedJobForModal.status !== 'completed' && (
                      <button
                        className="jm-btn-sm status-enroute"
                        onClick={() => adminSetJobStatus('en_route')}
                        title="Mark this job as En Route"
                      >
                        <Navigation size={14} /> En Route
                      </button>
                    )}
                    {selectedJobForModal.status !== 'in_progress' &&
                      selectedJobForModal.status !== 'completed' && (
                      <button
                        className="jm-btn-sm status-inprogress"
                        onClick={() => adminSetJobStatus('in_progress')}
                        title="Mark this job as In Progress"
                      >
                        <Clock size={14} /> In Progress
                      </button>
                    )}
                    {selectedJobForModal.status !== 'completed' && (
                      <button
                        className="jm-btn-sm status-completed"
                        onClick={() => {
                          if (window.confirm(`Mark ${selectedJobForModal.clientName} as Completed?`)) {
                            adminSetJobStatus('completed');
                          }
                        }}
                        title="Mark this job as Completed"
                      >
                        <Check size={14} /> Completed
                      </button>
                    )}
                    {selectedJobForModal.status !== 'scheduled' && (
                      <button
                        className="jm-btn-sm status-revert"
                        onClick={() => {
                          if (window.confirm('Revert this job back to Scheduled? This will clear clock-in/out timestamps.')) {
                            adminSetJobStatus('scheduled');
                          }
                        }}
                        title="Revert this job back to Scheduled"
                      >
                        <RefreshCw size={14} /> Revert
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Row 2: Client Communication — clearly labeled */}
              <div className="jm-comm-group">
                <div className="jm-comm-label">Email Client</div>
                <div className="jm-comm-row">
                  <button className="jm-btn reminder" onClick={() => {
                    setSelectedJobForModal(null);
                    if (onSendReminder) onSendReminder(selectedJobForModal);
                  }}>
                    <Bell size={15} /> Apt Reminder
                  </button>
                  <button className="jm-btn notify" onClick={(e) => { e.stopPropagation(); setShowNotify(true); setNotifyType('booked'); }}>
                    <Mail size={15} /> Booking Confirm
                  </button>
                </div>
              </div>

              {/* Row 3: Danger zone — separated, smaller */}
              <div className="jm-danger-zone">
                <button className="jm-btn-sm running-behind" onClick={() => {
                  const jobRef = selectedJobForModal;
                  setSelectedJobForModal(null);
                  if (onRunningBehind) onRunningBehind(jobRef);
                }}>
                  <Clock size={14} /> Late
                </button>
                <button className="jm-btn-sm reschedule" onClick={() => {
                  setSelectedJobForModal(null);
                  if (onOpenReschedule) onOpenReschedule(selectedJobForModal);
                }}>
                  <Calendar size={14} /> Reschedule
                </button>
                <button className="jm-btn-sm delete" onClick={async () => {
                  if (selectedJobForModal.recurringScheduleId && onDeleteSeries) {
                    setShowDeleteSeriesConfirm(true);
                  } else {
                    if (window.confirm(`Delete job for ${selectedJobForModal.clientName}? This cannot be undone.`)) {
                      if (onDeleteJob) await onDeleteJob(selectedJobForModal.id);
                      setSelectedJobForModal(null);
                    }
                  }
                }}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>

              {/* Series Delete Options */}
              {showDeleteSeriesConfirm && selectedJobForModal?.recurringScheduleId && (
                <div className="series-delete-inline" style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: '10px', marginTop: '10px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#991b1b', fontWeight: 500 }}>
                    This job is part of a recurring series.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      className="jm-btn-sm delete"
                      onClick={async () => {
                        if (window.confirm('Delete just this one job?')) {
                          if (onDeleteJob) await onDeleteJob(selectedJobForModal.id);
                          setShowDeleteSeriesConfirm(false);
                          setSelectedJobForModal(null);
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      Just This One
                    </button>
                    <button 
                      className="jm-btn-sm delete"
                      onClick={async () => {
                        if (window.confirm('Delete ALL scheduled jobs in this series? Completed jobs will be kept.')) {
                          if (onDeleteSeries) await onDeleteSeries(selectedJobForModal.recurringScheduleId);
                          setShowDeleteSeriesConfirm(false);
                          setSelectedJobForModal(null);
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      Entire Series
                    </button>
                    <button 
                      className="jm-btn-sm"
                      onClick={() => setShowDeleteSeriesConfirm(false)}
                      style={{ flex: '0 0 auto', background: '#f1f5f9', color: '#475569' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {photoViewer && (
        <div className="jm-photo-viewer-overlay" onClick={() => setPhotoViewer(null)}>
          <div className="jm-photo-viewer-modal" onClick={e => e.stopPropagation()}>
            <button className="jm-photo-viewer-close" onClick={() => setPhotoViewer(null)}><X size={24} /></button>
            <img src={photoViewer.url} alt={photoViewer.type || 'Photo'} />
            <div className="jm-photo-viewer-bar">
              <div className="jm-photo-viewer-info">
                <span className="jm-photo-viewer-type">{photoViewer.type?.replace('_', ' ') || 'Photo'}</span>
                {photoViewer.uploadedAt && (
                  <span className="jm-photo-viewer-time">
                    {(() => { try { return format(parseISO(photoViewer.uploadedAt), 'MMM d, h:mm a'); } catch { return ''; } })()}
                  </span>
                )}
                {photoViewer.employeeId && employees && (() => {
                  const emp = employees.find(e => e.id === photoViewer.employeeId);
                  return emp ? <span className="jm-photo-viewer-who">by {emp.firstName} {emp.lastName}</span> : null;
                })()}
              </div>
              <button
                className="jm-photo-download-btn"
                onClick={async () => {
                  try {
                    const response = await fetch(photoViewer.url);
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = photoViewer.filename || `${photoViewer.type || 'photo'}_${Date.now()}.jpg`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    window.open(photoViewer.url, '_blank');
                  }
                }}
              >
                <Download size={16} /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Series Update Prompt */}
      {seriesPrompt && selectedJobForModal && (
        <div className="modal-overlay job-detail-modal-overlay" style={{ zIndex: 2100 }} onClick={() => setSeriesPrompt(null)}>
          <div className="job-detail-modal series-prompt-modal" onClick={e => e.stopPropagation()}>
            <div className="job-modal-header">
              <div className="header-left"><h3>Update Series?</h3></div>
              <div className="header-right">
                <button className="close-btn" onClick={() => setSeriesPrompt(null)}><X size={18} /></button>
              </div>
            </div>
            <div className="job-modal-body" style={{ padding: '16px 20px' }}>
              <p style={{ color: '#374151', lineHeight: 1.5, marginBottom: 12 }}>
                {seriesPrompt.field === 'notes'
                  ? <>This client has upcoming appointments. Update notes for <strong>just this appointment</strong> or <strong>all future appointments</strong>?</>
                  : <>This job is part of a recurring series. Update <strong>just this appointment</strong> or <strong>this & all future</strong>?</>
                }
              </p>
              <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: 16 }}>
                Changing: <strong>{seriesPrompt.field === 'notes' ? 'Job Notes' : 'Job Type'}</strong>
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="modal-action-btn invoice-btn" style={{ justifyContent: 'center' }} 
                  onClick={() => doSaveField(selectedJobForModal.id, seriesPrompt.field, seriesPrompt.value, false)} disabled={savingEdit}>
                  Just This Appointment
                </button>
                <button className="modal-action-btn recurring-btn" style={{ justifyContent: 'center' }}
                  onClick={() => doSaveField(selectedJobForModal.id, seriesPrompt.field, seriesPrompt.value, true)} disabled={savingEdit}>
                  This & All Future Appointments
                </button>
                <button className="modal-action-btn reschedule-btn" style={{ justifyContent: 'center' }}
                  onClick={() => { setSeriesPrompt(null); setEditingField(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notify Client Modal */}
      {showNotify && selectedJobForModal && (
        <div className="modal-overlay job-detail-modal-overlay" style={{ zIndex: 2100 }} onClick={() => setShowNotify(false)}>
          <div className="job-detail-modal notify-client-modal" onClick={e => e.stopPropagation()}>
            <div className="job-modal-header">
              <div className="header-left"><h3>Notify Client</h3></div>
              <div className="header-right">
                <button className="close-btn" onClick={() => setShowNotify(false)}><X size={18} /></button>
              </div>
            </div>
            <div className="job-modal-body" style={{ padding: '16px 20px' }}>
              <p style={{ color: '#374151', marginBottom: 12 }}>
                Send a notification to <strong>{selectedJobForModal.clientName}</strong>:
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button
                  className={`modal-action-btn ${notifyType === 'booked' ? 'invoice-btn' : 'reschedule-btn'}`}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => setNotifyType('booked')}
                >
                  ✅ Job Booked
                </button>
                <button
                  className={`modal-action-btn ${notifyType === 'rescheduled' ? 'invoice-btn' : 'reschedule-btn'}`}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => setNotifyType('rescheduled')}
                >
                  📅 Rescheduled
                </button>
              </div>
              <div style={{ background: '#f8f7f5', padding: 12, borderRadius: 8, fontSize: '0.8rem', color: '#374151', marginBottom: 12 }}>
                <p style={{ margin: '3px 0' }}><strong>Date:</strong> {format(parseISO(selectedJobForModal.date), 'EEE, MMM d, yyyy')}</p>
                <p style={{ margin: '3px 0' }}><strong>Time:</strong> {formatJobTime(selectedJobForModal.scheduledTime)}</p>
                <p style={{ margin: '3px 0' }}><strong>Service:</strong> {serviceTypes[selectedJobForModal.serviceType]?.label || selectedJobForModal.serviceType}</p>
              </div>
              <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 14 }}>
                An email will be sent confirming their appointment details.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="modal-action-btn reschedule-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowNotify(false)}>
                  Cancel
                </button>
                <button className="modal-action-btn invoice-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={handleNotifyClient} disabled={sendingNotify}>
                  {sendingNotify ? <><Loader size={14} className="spin" /> Sending...</> : <><MessageSquare size={14} /> Send</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Job Card Component
function JobCard({
  job,
  color,
  stopNumber,
  onHover,
  onClick,
  onMove,
  onReschedule,
  hasFutureJobs,
  allJobs,
  drivingTime,
  employees
}) {
  const serviceType = serviceTypes[job.serviceType] || { label: 'Clean', color: '#87a086' };
  const jobStatus = jobStatuses?.[job.status] || { label: 'Scheduled', bg: '#dbeafe', color: '#1e40af' };

  // Priority detection
  const isFirstClean = allJobs ? !allJobs.some(j =>
    j.clientId === job.clientId && j.id !== job.id &&
    (j.status === 'completed' || (j.date && j.date < job.date))
  ) : false;
  const isRunningLate = !!job.runningBehind;

  // Billing color: red=unbilled, yellow=invoiced, green=paid
  const getBillingColor = () => {
    if (!job.invoiceId) return '#ef4444';
    if (job.invoicePaid || job.invoiceStatus === 'paid') return '#22c55e';
    return '#f59e0b';
  };

  // Build priority class
  const priorityClass = isRunningLate ? 'priority-running-late'
    : isFirstClean ? 'priority-first-clean' : '';

  // Staff recommendations based on client history
  const recommendation = useMemo(() => {
    if (!allJobs || !job.clientId) return null;

    // Find completed jobs for this client, sorted by date descending
    const pastJobs = allJobs
      .filter(j => j.clientId === job.clientId && j.id !== job.id && j.status === 'completed' && j.team?.length > 0)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

    if (pastJobs.length === 0) return null;

    // Last team that serviced this client
    const lastJob = pastJobs[0];
    const lastTeamNames = lastJob.teamNames?.length > 0
      ? lastJob.teamNames
      : lastJob.team?.map(empId => {
          const emp = employees?.find(e => e.id === empId);
          return emp ? `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.` : null;
        }).filter(Boolean);

    // Count visits per staff member across all completed jobs for this client
    const staffCounts = {};
    pastJobs.forEach(j => {
      (j.team || []).forEach(empId => {
        staffCounts[empId] = (staffCounts[empId] || 0) + 1;
      });
    });

    // Find the most frequent staff member
    let topStaffId = null;
    let topCount = 0;
    Object.entries(staffCounts).forEach(([empId, count]) => {
      if (count > topCount) {
        topStaffId = empId;
        topCount = count;
      }
    });

    let topStaffName = null;
    if (topStaffId) {
      const emp = employees?.find(e => e.id === topStaffId);
      if (emp) {
        topStaffName = `${emp.firstName} ${emp.lastName?.charAt(0) || ''}.`;
      } else {
        // Fallback: find name from teamNames in past jobs
        const jobWithStaff = pastJobs.find(j => j.team?.includes(topStaffId) && j.teamNames?.length > 0);
        if (jobWithStaff) {
          const idx = jobWithStaff.team.indexOf(topStaffId);
          topStaffName = jobWithStaff.teamNames[idx] || null;
        }
      }
    }

    return {
      lastTeam: lastTeamNames?.length > 0 ? lastTeamNames.join(' & ') : null,
      lastDate: lastJob.date,
      topStaff: topStaffName,
      topVisits: topCount,
      totalVisits: pastJobs.length
    };
  }, [allJobs, job.clientId, job.id, employees]);

  return (
    <div
      id={`job-card-${job.id}`}
      className={`route-job-card ${priorityClass}`}
      style={{ '--card-color': color }}
      onMouseEnter={() => onHover(job)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(job)}
    >
      {/* Driving Time Badge */}
      {drivingTime && (
        <div className="driving-time">
          <Car size={12} />
          {drivingTime}
        </div>
      )}
      
      <div className="job-card-content">
        {/* Header: Time & Stop Number */}
        <div className="job-card-header">
          <div className="job-time-block">
            <span className="job-time-main">{formatJobTime(job.scheduledTime)}</span>
            <span className="job-time-details">{job.duration}h duration</span>
          </div>
          {stopNumber && (
            <span className="job-stop-badge" style={{ backgroundColor: color }}>
              {stopNumber}
            </span>
          )}
        </div>

        {/* Client Info */}
        <div className="job-client-section">
          <h4 className="job-client-name">{job.clientName}</h4>
          <p className="job-address-text">
            <MapPin size={12} />
            {job.address}
          </p>
        </div>

        {/* Tags */}
        <div className="job-tags-row">
          <span className="job-tag service" style={{ backgroundColor: serviceType.color }}>
            {serviceType.label}
          </span>
          <span className="job-tag status" style={{ backgroundColor: jobStatus.bg, color: jobStatus.color }}>
            {jobStatus.label}
          </span>
          {isFirstClean && (
            <span className="job-tag first-clean">First Clean</span>
          )}
          {isRunningLate && (
            <span className="job-tag running-late">Late {job.runningBehindDelay ? `${job.runningBehindDelay}m` : ''}</span>
          )}
          {job.reminderSent && (
            <span className="job-tag reminder-sent">Reminded</span>
          )}
          {!hasFutureJobs && (
            <span className="job-tag rebook">Needs Rebook</span>
          )}
          {(job.photos?.length > 0 || job.damageReports?.length > 0) && (
            <span className="job-tag photos">
              <Camera size={10} /> {job.photos?.length || 0}
            </span>
          )}
        </div>

        {/* Quick note callout — surfaces job-level constraints prominently
            (e.g. "leaves by 3pm", "gate code 1234", "client needs reschedule") */}
        {job.notes && job.notes.trim() && (
          <div className="job-quick-note" title={job.notes}>
            <StickyNote size={11} />
            <span>{job.notes}</span>
          </div>
        )}

        {/* Staff Recommendations */}
        {recommendation && (
          <div className="job-recommendation">
            <div className="job-recommendation-header">
              <Star size={11} />
              <span>Recommended</span>
            </div>
            <div className="job-recommendation-details">
              {recommendation.lastTeam && (
                <div className="recommendation-row" title={`Last cleaned on ${recommendation.lastDate || 'unknown date'}`}>
                  <History size={11} />
                  <span>Last team: <strong>{recommendation.lastTeam}</strong></span>
                </div>
              )}
              {recommendation.topStaff && (
                <div className="recommendation-row">
                  <Star size={11} />
                  <span>Most frequent: <strong>{recommendation.topStaff}</strong> ({recommendation.topVisits}/{recommendation.totalVisits} visits)</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer: Price & Actions */}
        <div className="job-card-footer">
          <div className="job-price-section">
            {job.sqft > 0 && <span className="job-sqft">{job.sqft.toLocaleString()} sqft</span>}
            <span className="job-billing-icon" style={{ color: getBillingColor() }}>
              <DollarSign size={16} />
            </span>
            <span className="job-price">${job.price || 0}</span>
          </div>

          <div className="job-move-buttons">
            <button
              className="job-move-btn"
              onClick={(e) => { e.stopPropagation(); onMove(job.id, 'left'); }}
              title="Move to previous route"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              className="job-move-btn"
              onClick={(e) => { e.stopPropagation(); onMove(job.id, 'right'); }}
              title="Move to next route"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouteMapView;
